import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import MapTouchGate from '../map/MapTouchGate';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Radar, Play, Pause, Loader2 } from 'lucide-react';
import { formatTimeInTz, useOrgTimezone } from '../../utils/timezone';

// RainViewer free public API — no key required.
// Returns past + nowcast frames; tile URL pattern is host + path.
// https://www.rainviewer.com/api/weather-maps-api.html
interface RainViewerFrame {
  time: number; // unix seconds
  path: string; // e.g. "/v2/radar/1730000000"
}
interface RainViewerResponse {
  version: string;
  generated: number;
  host: string;
  radar: { past: RainViewerFrame[]; nowcast: RainViewerFrame[] };
}

const FRAMES_URL = 'https://api.rainviewer.com/public/weather-maps.json';
const COLOR_SCHEME = 4; // 0..8 — 4 is the default "Universal Blue"
const SMOOTH = 1;
const SNOW = 1;
const TILE_SIZE = 256;

// Restricted Lucide-style location pin marker
const pinIcon = L.divIcon({
  className: '',
  html: `<div style="font-size:28px;line-height:28px;transform:translate(-50%,-100%);filter:drop-shadow(0 1px 1px rgba(0,0,0,0.4))">📍</div>`,
  iconSize: [28, 28],
  iconAnchor: [0, 0],
});

function Recentre({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    // No animation: a zoom still in flight when the page unmounts throws from Leaflet.
    map.setView([lat, lng], map.getZoom(), { animate: false });
  }, [lat, lng, map]);
  return null;
}

// Pre-create one TileLayer per radar frame once, then animate by flipping
// opacity. This lets the browser cache each frame's tiles instead of refetching
// on every cycle, and stays well within RainViewer's free-tier rate limits.
function RadarOverlay({
  frames, host, frameIdx, opacity,
}: {
  frames: RainViewerFrame[];
  host: string;
  frameIdx: number;
  opacity: number;
}) {
  const map = useMap();
  const layersRef = useRef<L.TileLayer[]>([]);
  const signature = useMemo(
    () => frames.map((f) => f.path).join('|'),
    [frames],
  );

  useEffect(() => {
    layersRef.current.forEach((l) => map.removeLayer(l));
    layersRef.current = [];
    if (!host || frames.length === 0) return;
    layersRef.current = frames.map((f) => {
      const url = `${host}${f.path}/${TILE_SIZE}/{z}/{x}/{y}/${COLOR_SCHEME}/${SMOOTH}_${SNOW}.png`;
      const layer = L.tileLayer(url, {
        opacity: 0,
        attribution: 'Radar © RainViewer',
        maxNativeZoom: 12,
        maxZoom: 18,
      });
      layer.addTo(map);
      return layer;
    });
    return () => {
      layersRef.current.forEach((l) => map.removeLayer(l));
      layersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, host, map]);

  useEffect(() => {
    layersRef.current.forEach((layer, i) => {
      layer.setOpacity(i === frameIdx ? opacity : 0);
    });
  }, [frameIdx, opacity]);

  return null;
}

export default function RadarCard({ lat, lng, label }: { lat: number; lng: number; label?: string }) {
  const orgTz = useOrgTimezone();
  const [data, setData] = useState<RainViewerResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [frameIdx, setFrameIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetch(FRAMES_URL)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((json: RainViewerResponse) => {
        if (cancelled) return;
        setData(json);
        // Default to the latest "past" frame (most recent observation)
        setFrameIdx(json.radar.past.length - 1);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Failed to load radar');
      });
    return () => { cancelled = true; };
  }, []);

  const frames = useMemo(() => {
    if (!data) return [] as RainViewerFrame[];
    return [...data.radar.past, ...data.radar.nowcast];
  }, [data]);

  // Animate through frames every 800ms — slow enough to be readable,
  // and slow enough that already-cached tiles stay cached between cycles.
  useEffect(() => {
    if (!playing || frames.length === 0) return;
    intervalRef.current = window.setInterval(() => {
      setFrameIdx((i) => (i + 1) % frames.length);
    }, 800);
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [playing, frames.length]);

  const currentFrame = frames[frameIdx] || null;
  const isNowcast = data && currentFrame && data.radar.nowcast.includes(currentFrame);
  const frameTime = currentFrame
    ? formatTimeInTz(new Date(currentFrame.time * 1000).toISOString(), orgTz)
    : '';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Radar className="h-5 w-5 text-sky-600" /> Radar
          {label && <span className="text-sm font-normal text-neutral-500 ml-1 truncate">· {label}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="text-sm text-rose-600">Radar unavailable: {error}</div>
        )}
        {!data && !error && (
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading radar frames…
          </div>
        )}
        {data && (
          <>
            <div className="h-72 rounded-lg overflow-hidden border border-neutral-200">
              <MapContainer
                center={[lat, lng]}
                zoom={7}
                minZoom={4}
                maxZoom={11}
                scrollWheelZoom={false}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <RadarOverlay frames={frames} host={data.host} frameIdx={frameIdx} opacity={0.7} />
                <Marker position={[lat, lng]} icon={pinIcon} />
                <Recentre lat={lat} lng={lng} />
                <MapTouchGate />
              </MapContainer>
            </div>

            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-sky-600 text-white hover:bg-sky-700"
                aria-label={playing ? 'Pause' : 'Play'}
              >
                {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                {playing ? 'Pause' : 'Play'}
              </button>
              <input
                type="range"
                min={0}
                max={frames.length - 1}
                value={frameIdx}
                onChange={(e) => { setPlaying(false); setFrameIdx(Number(e.target.value)); }}
                className="flex-1 accent-sky-600"
                aria-label="Radar frame"
              />
              <div className="text-xs font-mono text-neutral-600 min-w-[5.5rem] text-right">
                {frameTime}
                {isNowcast && <span className="ml-1 text-amber-600">(forecast)</span>}
              </div>
            </div>
            <div className="mt-1 text-[11px] text-neutral-400">
              Past 2 h + 30 min nowcast · tiles by RainViewer
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
